import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Loader2,
  Settings as SettingsIcon,
  AlertCircle,
  History,
  ChevronDown,
  Trash2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { fmtDateTime, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";

const SOLID = "bg-foreground text-background hover:bg-foreground/90";

export default function AiActionButton({
  action,
  params = {},
  label,
  title,
  description,
  testId = "ai-action-btn",
  disabled = false,
  buttonClassName,
  historyQuery = null,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [needsSetup, setNeedsSetup] = useState(false);

  const [histOpen, setHistOpen] = useState(false);
  const [hist, setHist] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchHistory = async () => {
    setHistLoading(true);
    try {
      const { data } = await api.get("/ai/reflections", { params: historyQuery || {} });
      setHist(data);
    } catch (e) {
      toast.error?.(formatApiError(e));
      setHist([]);
    } finally {
      setHistLoading(false);
    }
  };

  const run = async () => {
    setOpen(true);
    setLoading(true);
    setResult(null);
    setError(null);
    setNeedsSetup(false);
    setHistOpen(false);
    setHist(null);
    setExpandedId(null);
    try {
      const { data } = await api.post(`/ai/actions/${action}`, { params });
      setResult(data.markdown);
      setMeta({ provider: data.provider, model: data.model });
    } catch (e) {
      const status = e?.response?.status;
      const msg = formatApiError(e);
      if (status === 400 && /set up|api key|configured|server url/i.test(msg)) setNeedsSetup(true);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const toggleHistory = async () => {
    const next = !histOpen;
    setHistOpen(next);
    if (next && hist === null) await fetchHistory();
  };

  const removeReflection = async (id) => {
    try {
      await api.delete(`/ai/reflections/${id}`);
      setHist((h) => (h || []).filter((r) => r.id !== id));
      if (expandedId === id) setExpandedId(null);
    } catch (e) {
      toast.error?.(formatApiError(e));
    }
  };

  return (
    <>
      <button
        onClick={run}
        disabled={disabled}
        data-testid={testId}
        className={
          buttonClassName ||
          `group flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium disabled:opacity-40 transition-colors duration-200 ${SOLID}`
        }
      >
        <Sparkles className="w-4 h-4 transition-transform duration-300 group-hover:rotate-12" />
        {label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto quiet-scroll" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-serif text-2xl">
              <Sparkles className="w-5 h-5 text-muted-foreground" /> {title}
            </DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>

          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid={`${testId}-loading`}>
              <Loader2 className="w-7 h-7 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Thinking it through…</p>
            </div>
          )}

          {!loading && error && (
            <div className="py-8 text-center" data-testid={`${testId}-error`}>
              <AlertCircle className="w-7 h-7 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-foreground mb-4">{error}</p>
              {needsSetup && (
                <Link
                  to="/settings"
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors duration-200"
                  data-testid={`${testId}-setup-link`}
                >
                  <SettingsIcon className="w-4 h-4" /> Set up AI in Settings
                </Link>
              )}
            </div>
          )}

          {!loading && result && (
            <div data-testid={`${testId}-result`}>
              <Markdown>{result}</Markdown>
              {meta && (
                <p className="text-[11px] text-muted-foreground mt-4">
                  Generated by {meta.provider} · {meta.model} · saved to your reflections
                </p>
              )}
            </div>
          )}

          {historyQuery && !loading && (
            <div className="mt-5 pt-4 border-t border-border" data-testid={`${testId}-history`}>
              <button
                onClick={toggleHistory}
                data-testid={`${testId}-history-toggle`}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
              >
                <History className="w-4 h-4" />
                Past reflections{hist ? ` (${hist.length})` : ""}
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${histOpen ? "rotate-180" : ""}`} />
              </button>

              {histOpen && (
                <div className="mt-3">
                  {histLoading ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                  ) : (hist || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground italic py-3" data-testid={`${testId}-history-empty`}>
                      No saved reflections yet — this is your first.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {hist.map((r) => (
                        <div key={r.id} className="rounded-lg border border-border/70" data-testid={`${testId}-history-item-${r.id}`}>
                          <button
                            onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                            className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left"
                          >
                            <span className="text-sm text-foreground">
                              {fmtDateTime(r.created_at)}
                              {r.week_start ? ` · week of ${fmtDate(r.week_start)}` : ""}
                            </span>
                            <span className="flex items-center gap-2 shrink-0">
                              <span className="text-[11px] text-muted-foreground hidden sm:inline">{r.model}</span>
                              <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${expandedId === r.id ? "rotate-180" : ""}`} />
                            </span>
                          </button>
                          {expandedId === r.id && (
                            <div className="px-3 pb-3 border-t border-border/60 pt-2">
                              <Markdown>{r.markdown}</Markdown>
                              <button
                                onClick={() => removeReflection(r.id)}
                                data-testid={`${testId}-history-delete-${r.id}`}
                                className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors duration-200"
                              >
                                <Trash2 className="w-3.5 h-3.5" /> Delete this reflection
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
